import {
  Radio as AriaRadio,
  type RadioProps as AriaRadioProps,
} from "react-aria-components";
import { cva, type VariantProps } from "class-variance-authority";
import { tw } from "src/design-system/tw";
import { RadioLabel, type RadioLabelProps } from "./RadioLabel";

const radioVariants = cva(
  [
    "flex w-full cursor-pointer items-center justify-center gap-4 rounded-md px-3 py-2.5 align-middle text-text-primary",
    "data-hovered:bg-neutral-600",
    "data-disabled:cursor-default data-disabled:opacity-50",
  ],
  {
    variants: {
      radioAlign: {
        left: [
          "before:box-border before:block before:h-5 before:w-5 before:rounded-full before:border-[0.15rem] before:border-solid before:border-border before:bg-surface before:transition-all before:duration-100",
          "data-selected:before:border-[0.435rem]",
          "data-pressed:before:border-input-border",
          "data-hovered:not-data-selected:before:border-border-hover",
          "data-focus-visible:before:outline-2 data-focus-visible:before:outline-offset-2 data-focus-visible:before:outline-ring data-focus-visible:before:transition-none data-focus-visible:before:outline-solid",
        ],
        right: [
          "justify-between",
          "after:box-border after:block after:h-5 after:w-5 after:rounded-full after:border-[0.15rem] after:border-solid after:border-border after:bg-surface after:transition-all after:duration-100",
          "data-selected:after:border-[0.435rem]",
          "data-pressed:after:border-input-border",
          "data-hovered:not-data-selected:after:border-border-hover",
          "data-focus-visible:after:outline-2 data-focus-visible:after:outline-offset-2 data-focus-visible:after:outline-ring data-focus-visible:after:transition-none data-focus-visible:after:outline-solid",
        ],
      },
      radioColor: {
        pink: "",
        dark: "",
      },
      hideRadio: {
        true: [
          "before:hidden after:hidden",
          "data-focus-visible:outline-2 data-focus-visible:outline-offset-2 data-focus-visible:outline-ring data-focus-visible:transition-none data-focus-visible:outline-solid",
          "data-hovered:rounded-lg data-hovered:bg-surface-hover",
        ],
        false: "",
      },
    },
    compoundVariants: [
      {
        radioAlign: "left",
        radioColor: "dark",
        class: "data-selected:before:border-border-selected",
      },
      {
        radioAlign: "right",
        radioColor: "dark",
        class: "data-selected:after:border-border-selected",
      },
      {
        radioAlign: "left",
        radioColor: "pink",
        class: "data-selected:before:border-border-brand-selected",
      },
      {
        radioAlign: "right",
        radioColor: "pink",
        class: "data-selected:after:border-border-brand-selected",
      },
    ],
    defaultVariants: {
      radioAlign: "left",
      radioColor: "pink",
      hideRadio: false,
    },
  },
);

type RadioProps = AriaRadioProps &
  VariantProps<typeof radioVariants> &
  RadioLabelProps & {
    hideRadio?: boolean; //TODO: Remove this prop and update existing components to use a Select component once we've built that.
    radioGroupValue?: string;
  } & {
    children?: React.ReactNode;
  };

/**
 * A radio input lets users pick one option from several choices. Usually presented within a radio group.
 * The label and secondary text will be passed into a custom child component, RadioLabel.
 *
 * @param value - The value of the radio button, used when submitting an HTML form.
 * @param className - Additional classes to apply to the radio.
 * @param radioAlign - 'left' or 'right'. Changes which side the radio button is on.
 * @param radioColor - 'pink' or 'dark'. Changes the color of the radio button.
 * @param isDisabled - Whether the radio is disabled.
 * @param radioLabel - The main text of the radio.
 * @param radioSecondaryText - The secondary text of the radio, e.g., a price or distance.
 * @param tooltip - The tooltip text to display when hovering over the radio "i" icon.
 * @param hideRadio - Whether to hide the radio button.
 * @param radioGroupValue - The value of the radio group.
 */

export function Radio({
  value,
  className,
  radioAlign,
  radioColor,
  isDisabled,
  radioLabel,
  radioSecondaryText,
  tooltip,
  hideRadio,
  radioGroupValue,
  children,
}: RadioProps) {
  return (
    <AriaRadio
      value={value}
      className={tw(
        radioVariants({
          radioAlign,
          radioColor,
          className,
          hideRadio,
        }),
      )}
      isDisabled={isDisabled}
    >
      <RadioLabel
        radioLabel={radioLabel}
        radioSecondaryText={radioSecondaryText}
        tooltip={tooltip}
        isSelected={radioGroupValue === value}
      />
      {children}
    </AriaRadio>
  );
}
